/* eslint-disable react/no-unknown-property */
/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { calculateProgressValue } from '../Progress/Progress.helpers';
import makeSmartMatchMeterV2Styling from './makeSmartMatchMeterV2Styling';
import { confidenceConfig, CONFIDENCE_RANK } from './confidenceConfig';

const SPARKLE_DURATION_MS = 250;
const SPARKLE_DELAYS = [0, 150, 300];
const TOOLTIP_TEXT =
  "We're calculating your matches based on your unique requirements. The more we know, the higher our confidence to provide the right matches for you.";

// Maps the v1 saga's NULL value ('-') to the v2 config key 'empty'
const toConfidenceKey = (level) => (level === '-' ? 'empty' : level);

const SparkleIcon = ({ animating, size = 18, color = '#ffffff', styles }) => {
  const sparkleStyle = (originX, originY, delayIndex) => ({
    opacity: animating ? 0 : 1,
    transform: animating ? 'scale(0)' : 'scale(1)',
    transformOrigin: `${originX}px ${originY}px`,
    transition: `opacity ${SPARKLE_DURATION_MS}ms ease-out, transform ${SPARKLE_DURATION_MS}ms ease-out`,
    transitionDelay: `${SPARKLE_DELAYS[delayIndex]}ms`,
  });

  return (
    <svg
      css={styles.sparkleIcon(size, color)}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Large sparkle (appears first) */}
      <path
        d="M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25z"
        style={sparkleStyle(19, 5, 0)}
      />
      {/* Main star (appears second) */}
      <path
        d="M11.5 9.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12z"
        style={sparkleStyle(9, 12, 1)}
      />
      {/* Small sparkle (appears last) */}
      <path
        d="M19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25z"
        style={sparkleStyle(19, 19, 2)}
      />
    </svg>
  );
};

const MeterBadge = ({
  label,
  value = '-',
  bg = '#fefefe',
  borderColor = '#eeeeee',
  textColor = '#525252',
  dotColor,
  width,
  children,
  styles,
}) => (
  <div css={styles.badgeContainer(width)}>
    <span css={styles.badgeLabel}>{label}</span>
    <div css={styles.badgeWrapper}>
      {children}
      <div css={styles.badge(bg, borderColor, width)}>
        {dotColor && (
          <div css={styles.badgeDot} style={{ backgroundColor: dotColor }} />
        )}
        <span css={styles.badgeValue(textColor)}>{value}</span>
      </div>
    </div>
  </div>
);

const PulseOverlay = ({ active, color = '#e3f0db', styles }) => {
  if (!active) return null;

  return <div css={styles.pulseOverlay(color)} />;
};

const MeterProgressBar = ({ progress = 0, barColor = '#ff9900', styles }) => (
  <div css={styles.progressTrack}>
    <div css={styles.progressBar(progress, barColor)} />
  </div>
);

const InfoButton = ({ onClick, styles }) => (
  <button
    type="button"
    css={styles.infoButton}
    aria-label="More information about match calculation"
    onClick={onClick}
  >
    <svg
      css={styles.infoIcon}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  </button>
);

const Tooltip = ({ text = '', arrowPosition = 'center', styles }) => (
  <div css={styles.tooltip()} role="tooltip">
    <div css={styles.tooltipArrow(arrowPosition)} />
    {text}
  </div>
);

export const SmartMatchMeterV2 = ({ id, testId }) => {
  const showSmartMatchMeterV2 = useSelector(
    (store) => store.features?.showSmartMatchMeterV2
  );
  const matchingSupplierCount = useSelector(
    (store) => store.supplierMatches?.matchingSupplierCount
  );
  const confidenceLevel = useSelector(
    (store) => store.supplierMatches?.matchConfidence?.confidenceLevel ?? '-'
  );
  const currentPage = useSelector((store) => store.navigation.currentPage);
  const totalPages = useSelector((store) => store.navigation.totalPages);
  const submissionPageNumber = useSelector(
    (store) => store.formConfig?.submissionPageNumber
  );
  const exitPhase = useSelector((store) => store.transition?.exitPhase);
  const enterPhase = useSelector((store) => store.transition?.enterPhase);

  const [isVisible, setIsVisible] = useState(false);
  const [hasUserNavigated, setHasUserNavigated] = useState(false);

  useEffect(() => {
    if (exitPhase === false) {
      // On completion of the first half of a page change this boolean changes from true to false
      if (currentPage > 0 && hasUserNavigated === false) {
        setIsVisible(true); // Mount the component just before the next page has entered into the viewport (i.e. prior to the ENTER phase)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exitPhase]);

  useEffect(() => {
    if (enterPhase === false) {
      // On completion of a full page change this boolean changes from true to false
      if (currentPage > 0 && hasUserNavigated === false) {
        setHasUserNavigated(true); // The user has completed their first page change
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enterPhase]);

  useEffect(() => {
    if (
      hasUserNavigated === true &&
      isVisible === true &&
      confidenceLevel !== '-'
    ) {
      const fadeInTimer = setTimeout(() => {
        const smartMatchMeterContainer = document.querySelector(
          "[data-testid='smartMatchMeterTwo']"
        );
        // The container is absent when the meter is hidden or has already been
        // removed by a further page change, so there is nothing to fade in
        if (!smartMatchMeterContainer) return;

        // Kick off fade-in animation of the smartMatchMeterV2 container after the page change has completed
        smartMatchMeterContainer.style.opacity = 1;
      }, 0);

      return () => clearTimeout(fadeInTimer);
    }

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUserNavigated]);

  const confidence = toConfidenceKey(confidenceLevel);
  const styles = makeSmartMatchMeterV2Styling(confidence);
  const conf = confidenceConfig[confidence];

  const prevConfidence = useRef(confidence);
  const [pulsing, setPulsing] = useState(false);
  const [iconAnimating, setIconAnimating] = useState(true);
  const pulseTimer = useRef(null);
  const iconTimer = useRef(null);
  const [highestConfidence, setHighestConfidence] = useState(confidence);
  const [showTooltip, setShowTooltip] = useState(false);

  // Track highest confidence reached — bar colour never goes backwards
  useEffect(() => {
    if (CONFIDENCE_RANK[confidence] > CONFIDENCE_RANK[highestConfidence]) {
      setHighestConfidence(confidence);
    }
  }, [confidence, highestConfidence]);

  const barColor = confidenceConfig[highestConfidence].bar;

  // Animate icon on load
  useEffect(() => {
    iconTimer.current = setTimeout(() => setIconAnimating(false), 600);
    return () => {
      if (iconTimer.current) clearTimeout(iconTimer.current);
    };
  }, []);

  // Pulse on confidence change + re-trigger icon animation
  useEffect(() => {
    if (confidence !== prevConfidence.current) {
      prevConfidence.current = confidence;
      if (pulseTimer.current) clearTimeout(pulseTimer.current);
      setPulsing(true);
      setIconAnimating(true);
      pulseTimer.current = setTimeout(() => {
        setPulsing(false);
        setIconAnimating(false);
      }, 600);
    }
    return () => {
      if (pulseTimer.current) clearTimeout(pulseTimer.current);
    };
  }, [confidence]);

  if (!showSmartMatchMeterV2) return null;
  if (!isVisible) return null;

  const progress = calculateProgressValue(
    currentPage,
    totalPages,
    submissionPageNumber
  );
  const displayCount =
    matchingSupplierCount > 0 ? String(matchingSupplierCount) : '-';

  return (
    <div css={styles.root} data-testid="smartMatchMeterTwo">
      <div css={styles.card} id={id} data-testid={testId}>
        <div css={styles.header}>
          <div css={styles.leftSection}>
            <div css={styles.iconBox}>
              <SparkleIcon animating={iconAnimating} styles={styles} />
            </div>
            <div css={styles.title}>Smart Match Meter</div>
          </div>

          <div css={styles.rightSection}>
            <MeterBadge
              label="Matches"
              value={displayCount}
              borderColor={
                confidence === 'high' ? confidenceConfig.high.dot : '#eeeeee'
              }
              styles={styles}
            />

            <MeterBadge
              label="Confidence"
              value={conf.label}
              bg={conf.bg}
              borderColor={conf.border}
              textColor={conf.text}
              dotColor={confidence !== 'empty' ? conf.dot : undefined}
              width="80px"
              styles={styles}
            >
              <PulseOverlay active={pulsing} color={conf.bg} styles={styles} />
            </MeterBadge>

            <div
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              onFocus={() => setShowTooltip(true)}
              onBlur={() => setShowTooltip(false)}
            >
              <InfoButton styles={styles} />
            </div>
          </div>
        </div>

        <MeterProgressBar
          progress={progress}
          barColor={barColor}
          styles={styles}
        />
      </div>

      <div
        css={styles.tooltipPosition}
        aria-hidden={!showTooltip}
        style={showTooltip ? { opacity: 1, visibility: 'visible' } : undefined}
      >
        <Tooltip text={TOOLTIP_TEXT} arrowPosition="center" styles={styles} />
      </div>
    </div>
  );
};

export default SmartMatchMeterV2;
